import { useQuery } from "@tanstack/react-query";

import { useApiClient } from "../useApiClient";
import type { components } from "../schema";

export const IMPORT_JOBS_QUERY_KEY = ["import-jobs"] as const;
// The trash view (soft-deleted batches) is a distinct query so it never shares
// cache with the active recent-imports list / progress pill.
export const DELETED_IMPORT_JOBS_QUERY_KEY = ["import-jobs", "deleted"] as const;

export type ImportJobsResponse = components["schemas"]["ImportJobsResponse"];
export type ImportJob = components["schemas"]["ImportJobResponse"];

const ACTIVE_STATUSES: ReadonlySet<string> = new Set(["pending", "running"]);

/** A job still in flight (server hasn't recorded a terminal outcome yet). */
export function isImportJobActive(job: ImportJob): boolean {
  return ACTIVE_STATUSES.has(job.status);
}

// Poll while a job is in flight so the global progress pill (and the Import
// page's recent list) reflect server state without a manual refresh (ADR
// 0023). 2s (vs the original 5s) so the measured progress bar updates smoothly
// enough not to read as "stuck"; polling stops once every job is terminal -- no
// idle network churn.
const POLL_INTERVAL_MS = 2000;

/**
 * Recent import jobs for the signed-in tenant, newest first (L0-U15 / ADR 0023).
 * Refetches every 5s while any job is pending/running; idle otherwise. Because
 * job state lives server-side, navigating away and back re-attaches to the same
 * progress.
 */
export function useImportJobs() {
  const client = useApiClient();
  return useQuery({
    queryKey: IMPORT_JOBS_QUERY_KEY,
    queryFn: async () => {
      // ``active`` excludes soft-deleted batches so a deleted import vanishes
      // from the recent list + progress pill (it moves to the trash view).
      const { data, error } = await client.GET("/api/imports/jobs", {
        params: { query: { retention: "active" } },
      });
      if (error !== undefined) {
        throw new Error("Failed to load import jobs");
      }
      return data;
    },
    refetchInterval: (query) => {
      const jobs = query.state.data?.jobs ?? [];
      return jobs.some(isImportJobActive) ? POLL_INTERVAL_MS : false;
    },
  });
}

/**
 * Soft-deleted import batches (the trash). Restorable until ``restorable_until``,
 * after which a purge sweep removes them. Not polled -- the trash only changes on
 * an explicit delete / restore, which invalidate this query.
 */
export function useDeletedImportJobs() {
  const client = useApiClient();
  return useQuery({
    queryKey: DELETED_IMPORT_JOBS_QUERY_KEY,
    queryFn: async () => {
      const { data, error } = await client.GET("/api/imports/jobs", {
        params: { query: { retention: "soft_deleted" } },
      });
      if (error !== undefined) {
        throw new Error("Failed to load deleted imports");
      }
      return data;
    },
  });
}
